<script setup>
import { computed, ref } from 'vue';
import { usePuzzleStore } from '@/stores/puzzleStore';

const puzzleStore = usePuzzleStore();

// Convert tactics object into an array for the bar chart
// e.g. puzzleStore.tactics = { "Fork": {spotted:2, total:3}, ... }

// Tactics performance from store
const tacticsPerformance = computed(() => puzzleStore.tacticsPerformance);


// Solved percentage from store
const solvedPercentage = computed(() => puzzleStore.solvedPercentage);

// Convert openings array to e.g. [ { name: 'Caro-Kann', mistakes: 2 }, ... ]
const openings = computed(() => {
  return puzzleStore.openings.map(open => ({
    name: open[0],
    timesPlayed: open[1],
    mistakes: open[2],
  }));
});

const series = ref([solvedPercentage.value]);
const chartOptions = ref({
  chart: {
    height: 350,
    type: "radialBar",
    offsetY: -10,
    foreColor: '#EBECD0',
  },
  plotOptions: {
    radialBar: {
      startAngle: -135,
      endAngle: 135,
      track: {
        background: "#1d1d1d",
      },
      dataLabels: {
        name: {
          fontSize: "16px",
          color: "#ebecd0",
          offsetY: 120
        },
        value: {
          offsetY: 76,
          fontSize: "22px",
          color: "#ebecd0",
          formatter: function (val) {
            return val + "%";
          }
        },
      }
    }
  },
  fill: {
    colors: ['#bb86fc'],
  },
  stroke: {
    dashArray: 8
  },
  labels: ["Success Rate"],
});
</script>

<template>

  <div class="analysis-container d-flex align-items-center justify-content-center primary-font">
    <div class="d-flex flex-column">
      <div>
        <nav class="navbar-container d-flex align-items-center justify-content-between pt-3 pb-3">
          <span class="nav-item left search m-0" @click="$router.push({ name: 'search' })">&lt; NEW SEARCH</span>
        </nav>  
      </div>
      <div class="d-flex flex-column flex-lg-row gap-4">
  
        <!-- Left Panel: Tactics Performance -->
        <div class="panel">
          <h3 class="mb-4 text-center ps-lg-5 pe-lg-5">Tactics Performance</h3>
          <hr class="border-custom">
  
          <div
            v-for="tactic in tacticsPerformance"
            :key="tactic.name"
            class="mb-3"
          >
            <!-- Name Above the Progress Bar -->
            <div class="text-start mb-1 ps-1">
              {{ tactic.name }}
            </div>
  
            <!-- Progress Bar with Score Aligned Right -->
            <div class="d-flex align-items-center">
              <div class="progress progress-custom flex-grow-1">
                <div 
                  class="progress-bar progress-main" 
                  role="progressbar"
                  :style="{
                    width: tactic.total > 0 ? tactic.percentage + '%' : '100%',
                    background: tactic.total > 0 
                      ? '#bb86fc' 
                      : 'repeating-linear-gradient(45deg, #bb86fc, #bb86fc 10px, #1d1d1d 10px, #1d1d1d 20px)',
                    opacity: tactic.total > 0 ? '1' : '0.5'
                  }"
                  :aria-valuenow="tactic.percentage"
                  aria-valuemin="0"
                  aria-valuemax="100"
                ></div>
              </div>
              <!-- Score on the Right -->
              <div class="ms-3 text-end">
                {{ tactic.spotted }} / {{ tactic.total }}
              </div>
            </div>
          </div>
        </div>
  
        <!-- Right Panels -->
        <div class="d-flex flex-column gap-4">
  
          <!-- Successfully Solved -->
          <div class="panel text-center d-flex flex-column align-items-center justify-content-center">
            <h4>Puzzle Solving Performance</h4>
            <hr class="border-custom">
            <!-- Circular chart -->
            <div id="chart">
              <apex-chart
                type="radialBar"
                height="400"
                :options="chartOptions"
                :series="series"
              />
            </div>
          </div>
  
          <!-- Openings -->
          <div class="panel d-flex flex-column openings-panel flex-grow-1">
          <h4 class="text-center">Opening Mistakes</h4>
          <hr class="border-custom" />
  
          <!-- Scrollable Content (Only if more than 6 items) -->
          <div :class="{'content-area': openings.length > 6}">
            <div v-if="openings.length === 0" class="fst-italic clr-w">
              You have not made mistakes in openings!
            </div>
  
            <div 
              v-else 
              v-for="(opening, idx) in openings" 
              :key="idx" 
              class="d-flex justify-content-between mb-2"
            >
              <span class="pe-5"> 
                {{ opening.name }}
              </span>
              <span class="mistake-count ps-2">
                {{ opening.mistakes }} mistakes
              </span>
            </div>
          </div>
  
          <!-- Button stays at the bottom -->
          <button v-if="openings.length !== 0" class="btn btn-main mt-auto w-100" @click="$router.push({ name: 'blunders' })">
            Check opening mistakes
          </button>
        </div>
        </div>
      </div>
    </div>
    </div>
</template>

<style scoped>

.opening-name {
  word-wrap: break-word; 
  overflow-wrap: break-word;
  max-width: 60%;   
}

body {
  margin: 0;
  overflow-x: hidden;
}



.analysis-container {
  min-height: 100vh; /* Allow scrolling if needed */
  overflow-y: auto; /* Enables vertical scrolling */
}

.navbar-container {
    background: transparent; 
    font-size: 2rem;
    font-family: 'Oswald'; 
  }

 .search {
    color: #ebecd0; 
  }

 .analysis {
    color: #ebecd0; 
  }

  
  /* Navbar Items */
  .nav-item {
    cursor: pointer;
    transition: color 0.2s ease-in-out;
  }
  
  .nav-item:hover {
    color: #fff; 
  }


.mistake-count {
  white-space: nowrap
}

openings-panel {
  display: flex;
  flex-direction: column;
}

.content-area {
  flex-grow: 1;
  overflow-y: auto; /* Enables scroll when more than 6 entries */
  max-height: 200px;
  padding-right: 12px; /* Prevents scrollbar overlap */
  scrollbar-color: #ebecd0 #1d1d1d;
}

.panel {
  background-color: #2e2e2e;
  color: #ebecd0;
  border: 2px solid #bb86fc;
  box-shadow: #bb86fc 0px 0px 15px;
  border-radius: 8px;
  padding: 20px;
}

/* Border Styling */
.border-custom {
  border-color: #ebecd0 !important;
  width: 100%;
  margin: 1rem 0; /* Ensures spacing */
}

/* Progress Bar */
.progress-custom {
  background-color: #1d1d1d;
  height: 30px;
}

.progress-main {
  background-color: #bb86fc;
}

/* Button Styling */
.btn-main {
  background-color: #bb86fc;
  color: #1d1d1d;
  border: none;
}

.btn-main:hover {
  background-color: #a075e0;
  color: #1d1d1d;
}
</style>
